import { StripUnderscore, UnderscoreKeys } from "../util/Types";
import { BasicError } from "./BasicError";
import { StoryStatus, TopicChecklist } from "./Enums";
import { PageImage, StoryBase, storyBaseSchema, StoryImagePage, StoryInnerPage } from "./StoryBase";
import z from "zod";


export type PageNumberType = "front" | "back" | "dedication" | "end" | number;

export type TopicAnalysis = {
	simple: boolean;
	mainCharacter: boolean;
	namedCharacter: boolean;
	numberOfCharacters: number;
	presenceOfPlot: boolean;
	copyright : boolean;
};

export type Character = {
	name?: string;
	gender?: string;
	ageBracket?: string;
	mainCharacter?: boolean;
	typeOfCharacter?: string;
	visualDescription?: string;
	typeOfRelationship? : string;
	eyeColour?: string;
	hairColour?: string;
	hairStyle?: string;
	hairLength?: string;
	skinColour?: string;
	clothing?: string;
	userCreated?: boolean;
};

const pageNumberArray: PageNumberType[] = ["front", "dedication", ...Array.from({ length: 15 }, (_, i) => i + 1), "end", "back"];

export const storyUISchema = storyBaseSchema.extend({
  _promptSetKey: z.string().optional(),
  _imagePromptSetKey: z.string().optional(),
  _topic: z.string().optional(),
  _topicAnalysis: z.any().optional(),
  _characters: z.array(z.any()).optional(),
  _charactersToExclude: z.array(z.any()).optional(),
  _storyPages: z.array(z.any()).optional(),
  _storyImagePages: z.array(z.any()).optional(),
  _to: z.string().optional(),
  _from: z.string().optional(),
  _dedication: z.string().optional(),
});

export type StoryUIFields = StripUnderscore<z.infer<typeof storyUISchema>>;

export class StoryUI<TFields extends StoryUIFields = StoryUIFields> extends StoryBase<TFields> {
	_promptSetKey: string | undefined;
	_imagePromptSetKey: string | undefined;
	_topic: string | undefined;
	_topicAnalysis: TopicAnalysis | undefined;
	_characters: Character[] | undefined;
	_charactersToExclude: Character[] | undefined;
	_storyPages: StoryInnerPage[] | undefined;
	_storyImagePages: StoryImagePage[] | undefined;
	_to: string | undefined;
	_from: string | undefined;
	_dedication: string | undefined;
	override _className: string = "Story";
	override _deserializeClassName : string  = "StoryUI";

	override getFieldList(): (keyof UnderscoreKeys<TFields>)[] {
		return [
			...super.getFieldList(),
			"_promptSetKey",
			"_imagePromptSetKey",
			"_topic",
			"_topicAnalysis",
			"_characters",
			"_charactersToExclude",
			"_storyPages",
			"_storyImagePages",
			"_to",
			"_from",
			"_dedication",
		];
	}
	constructor(props?: Partial<TFields>) {
		super(props);
	}
	override validateSchema(): void {
		storyUISchema.parse(this);
	}
	get promptSetKey(): string | undefined {
		return this.checkFieldType<"promptSetKey">(this._promptSetKey);
	}
	set promptSetKey(value: string | undefined) {
		if (this._promptSetKey === value) return;
		this._promptSetKey = this.checkFieldTypeAndMark<"promptSetKey">("_promptSetKey", value);
		if(value === undefined){
			this.setStoryState(StoryStatus.NOT_STARTED)
		}else{
			this.setStoryState(StoryStatus.PROMPT_SET_CHOSEN);
		}
	}

	get imagePromptSetKey(): string | undefined {
		return this.checkFieldType<"imagePromptSetKey">(this._imagePromptSetKey);
	}
	set imagePromptSetKey(value: string | undefined) {
		if (this._imagePromptSetKey === value) return;
		this._imagePromptSetKey = this.checkFieldTypeAndMark<"imagePromptSetKey">("_imagePromptSetKey", value);
		if(value === undefined){
			this.setStoryState(StoryStatus.STORY_SET)
		}else{
			this.setStoryState(StoryStatus.IMAGE_PROMPT_SET_CHOSEN);
		}
	}

	get topic(): string | undefined {
		return this.checkFieldType<"topic">(this._topic);
	}
	set topic(value: string | undefined) {
		if (this._topic === value) return;
		this._topic = this.checkFieldTypeAndMark<"topic">("_topic", value);
		value === undefined ? this.setStoryState(StoryStatus.PROMPT_SET_CHOSEN) : this.setStoryState(StoryStatus.TOPIC_SET);
	}

	get topicAnalysis(): TopicAnalysis | undefined {
		return this.checkFieldType<"topicAnalysis">(this._topicAnalysis);
	}
	set topicAnalysis(value: TopicAnalysis | undefined) {
		if (this._topicAnalysis === value) return;
		this._topicAnalysis = this.checkFieldTypeAndMark<"topicAnalysis">("_topicAnalysis", value);
		value === undefined ? this.setStoryState(StoryStatus.TOPIC_SET) : this.setStoryState(StoryStatus.TOPIC_ANALYSED);
	}

	get characters(): Character[] | undefined {
		return this.checkFieldType<"characters">(this._characters);
	}
	set characters(value: Character[] | undefined) {
		if (this._characters === value) return;
		this._characters = this.checkFieldTypeAndMark<"characters">("_characters", value);
		if (value === undefined) {
			this.setStoryState(StoryStatus.TITLE_CHOSEN);
			//Or if at least one character has a style property set
		} else if (value.findIndex((char) => char.skinColour !== undefined) !== -1) {
			this.setStoryState(StoryStatus.CHARACTERS_APPEARANCE_SET);
		} else {
			this.setStoryState(StoryStatus.CHARACTERS_IDENTIFIED);
		}
	}

	get charactersToExclude(): Character[] | undefined {
		return this.checkFieldType<"charactersToExclude">(this._charactersToExclude);
	}
	set charactersToExclude(value: Character[] | undefined) {
		if (this._charactersToExclude === value) return;
		this._charactersToExclude = this.checkFieldTypeAndMark<"charactersToExclude">("_charactersToExclude", value);
		value === undefined ? this.setStoryState(StoryStatus.TITLE_CHOSEN) : this.setStoryState(StoryStatus.CHARACTERS_IDENTIFIED);
	}
	get to(): string | undefined {
		return this.checkFieldType<"to">(this._to);
	}
	set to(value: string | undefined) {
		if (this._to === value) return;
		value === undefined && this._from === undefined ? this.setStoryState(StoryStatus.CHARACTERS_SET) : this.setStoryState(StoryStatus.TO_FROM_SET);
		this._to = this.checkFieldTypeAndMark<"to">("_to", value);
	}

	get from(): string | undefined {
		return this.checkFieldType<"from">(this._from);
	}
	set from(value: string | undefined) {
		if (this._from === value) return;
		this._from = this.checkFieldTypeAndMark<"from">("_from", value);
		value === undefined && this._to === undefined ? this.setStoryState(StoryStatus.CHARACTERS_SET) : this.setStoryState(StoryStatus.TO_FROM_SET);
	}

	get dedication(): string | undefined {
		return this.checkFieldType<"dedication">(this._dedication);
	}
	set dedication(value: string | undefined) {
		if (this._dedication === value) return;
		this._dedication = this.checkFieldTypeAndMark<"dedication">("_dedication", value);
		value === undefined ? this.setStoryState(StoryStatus.TO_FROM_SET) : this.setStoryState(StoryStatus.DEDICATION_ADDED);
	}
	get storyPages(): StoryInnerPage[] | undefined {
		return this.checkFieldType<"storyPages">(this._storyPages);
	}
	set storyPages(value: StoryInnerPage[] | undefined) {
		if (this._storyPages === value) return;
		this._storyPages = this.checkFieldTypeAndMark<"storyPages">("_storyPages", value);
		value === undefined ? this.setStoryState(StoryStatus.DEDICATION_ADDED) : this.setStoryState(StoryStatus.STORY_WRITTEN);
	}
	get storyImagePages() : StoryImagePage[] | undefined {
		return this.checkFieldType<"storyImagePages">(this._storyImagePages);
	}
	set storyImagePages(value: StoryImagePage[] | undefined) {
		if (this._storyImagePages === value) return;
		this._storyImagePages = this.checkFieldTypeAndMark<"storyImagePages">("_storyImagePages", value);
		if (value === undefined) {
			this.setStoryState(StoryStatus.VISUAL_DESCRIPTIONS_PAGES);
		} else if (!this._storyPages?.length || value?.length < this._storyPages?.length) {
			this.setStoryState(StoryStatus.VISUAL_DESCRIPTIONS_PAGES);
		} else {
			this.setStoryState(StoryStatus.IMAGES_GENERATED);
		}
	}
	addCharacter(character: Character) {
		this._characters?.push(character);
		this.markFieldDirty("_characters");
	}
	removeCharacter(character: Character) {
		this._characters = this._characters?.filter((char) => char.name !== character.name);
		this.markFieldDirty("_characters");
	}
	addExcludedCharacter(character: Character) {
		if (!this._charactersToExclude) this._charactersToExclude = [];
		this._charactersToExclude.push(character);
		this.markFieldDirty("_charactersToExclude");
	}
	removeExcludedCharacter(character: Character) {
		this._charactersToExclude = this._charactersToExclude?.filter((char) => char.name !== character.name);
		this.markFieldDirty("_charactersToExclude");
	}
	get title(): string | undefined {
		const titleOptions = this._titleOptions;
		const selectedTitle = this._selectedTitle;
		if (titleOptions !== undefined && selectedTitle !== undefined && titleOptions.length >= selectedTitle)
			return titleOptions[selectedTitle - 1]?.title;
		return undefined;
	}
	get synopsis(): string | undefined {
		const titleOptions = this._titleOptions;
		const selectedTitle = this._selectedTitle;
		if (titleOptions !== undefined && selectedTitle !== undefined && titleOptions.length >= selectedTitle)
			return titleOptions[selectedTitle - 1]?.synopsis;
		return undefined;
	}
	set status(status: StoryStatus) {
		super.status = status;
	}
	get status(): StoryStatus {
		return this._status as StoryStatus;
	}
	addImageForPage(image: PageImage, page: "front" | "back" | number, setAsSelected: boolean = true) {
		switch (page) {
			case "front": {
				if (!this.frontCover) throw new BasicError("No front cover", "Cannot set image if visual description not already set");
				if (!this.frontCover.imageOptions) this.frontCover.imageOptions = [];
				const newFLength = this.frontCover.imageOptions.push(image);
				if (setAsSelected) this.frontCover.imageIndex = newFLength - 1;
				if (newFLength > 1) this.changeCount("imageRegenerations", 1);
				this.markFieldDirty("_frontCover");
				break;
			}
			case "back": {
				if (!this.backCover) throw new BasicError("No back cover", "Cannot set image if visual description not already set");
				if (!this.backCover.imageOptions) this.backCover.imageOptions = [];
				const newBLength = this.backCover.imageOptions.push(image);
				if (setAsSelected) this.backCover.imageIndex = newBLength - 1;
				if (newBLength > 1) this.changeCount("imageRegenerations", 1);
				this.markFieldDirty("_backCover");
				break;
			}
			default: {
				const pageIndex = page - 1;
				if (!this.storyImagePages || !this.storyImagePages[pageIndex]) throw new BasicError("No page", "Cannot set image if page doesn't exist");
				if (!this.storyImagePages[pageIndex].imageOptions) this.storyImagePages[pageIndex].imageOptions = [];
				const newPLength = this.storyImagePages[pageIndex].imageOptions.push(image);
				if (setAsSelected) this.storyImagePages[pageIndex].imageIndex = newPLength - 1;
				if (newPLength > 1) this.changeCount("imageRegenerations", 1);
				this.markFieldDirty("_storyImagePages");
				break;
			}
		}
	}
	getImageForPage(pageNumber: PageNumberType): PageImage | undefined {
		switch (pageNumber) {
			case "front":
				if (!this._frontCover || !this._frontCover.imageOptions || this._frontCover.imageIndex === undefined) return;
				return this._frontCover.imageOptions[this._frontCover.imageIndex];
			case "back":
				if (!this._backCover || !this._backCover.imageOptions || this._backCover.imageIndex === undefined) return;
				return this._backCover.imageOptions[this._backCover.imageIndex];
			case "dedication":
			case "end":
				return undefined;
			default: {
				if (!this._storyImagePages || !this._storyImagePages[pageNumber - 1]) return undefined;
				const page = this._storyImagePages[pageNumber - 1];
				if (!page || page.imageIndex === undefined || !page.imageOptions) return undefined;
				return page.imageOptions[page.imageIndex];
			}
		}
	}
	getTextForPage(pageNumber: number): string | undefined {
		if (!this._storyPages || !this._storyPages[pageNumber - 1]) return undefined;
		const page = this._storyPages[pageNumber - 1];
		if (!page || page.textIndex === undefined || !page.textOptions) return undefined;
		return page.textOptions[page.textIndex];
	}
	/**
	 * Set the index of the selected image for a given page
	 * @param pageNumber the page number
	 * @param imageIndex the index of the selected image
	 * @returns void
	 */
	setImageOptionForPage(pageNumber: "front" | "back" | number, imageIndex: number): void {
		this.setStoryState(StoryStatus.IMAGES_GENERATED);
		switch (pageNumber) {
			case "front":
				if (!this._frontCover) return;
				this._frontCover.imageIndex = imageIndex;
				this.markFieldDirty("_frontCover");
				break;
			case "back":
				if (!this._backCover) return;
				this._backCover.imageIndex = imageIndex;
				this.markFieldDirty("_backCover");
				break;
			default:
				if (!this._storyImagePages || !this._storyImagePages[pageNumber - 1]) return;
				const page = this._storyImagePages[pageNumber - 1];
				if (!page) throw new BasicError("No story page", "Can't set image option for page as page is undefined");
				page.imageIndex = imageIndex;
				this.markFieldDirty("_storyImagePages");
		}
	}
	setTextOptionForPage(pageNumber: number, textIndex: number) {
		if (!this._storyPages || !this._storyPages[pageNumber - 1])
			throw new BasicError("Page number invalid", "Not story page for pagenumber " + pageNumber);
		const page = this._storyPages[pageNumber - 1];
		if (!page) throw new BasicError("Page number invalid", "Not story page for pagenumber " + pageNumber);
		page.textIndex = textIndex;
		this.setStoryState(StoryStatus.STORY_WRITTEN);
		this.markFieldDirty("_storyPages");
	}
	getTopicAnalysisChecklist(): { title: string; isDefined: boolean; isValid: boolean; promptText? : string, invalidText?: string }[] {
		const items: { key: TopicChecklist, title: string; isDefined: boolean; isValid: boolean; promptText? : string, invalidText?: string }[] = [];
		items.push({
			key: TopicChecklist.SIMPLE,
			title: "Simple",
			isDefined: this.topicAnalysis?.simple !== undefined,
			isValid: this.topicAnalysis?.simple === true,
			promptText: "Simple topics make great stories",
			invalidText: "The topic may bit a bit too complicated for a short story",
		});
		items.push({
			key: TopicChecklist.MAIN_CHARACTER,
			title: "Main character",
			isDefined: this.topicAnalysis?.mainCharacter !== undefined,
			isValid: this.topicAnalysis?.mainCharacter === true,
			promptText: "Make sure you name a main character",
			invalidText: "You don't have a main character, we can make one up for you.",
		});
		items.push({
			key: TopicChecklist.MAIN_CHARACTER_NAME,
			title: "Names",
			isDefined: this.topicAnalysis?.namedCharacter !== undefined,
			isValid: this.topicAnalysis?.namedCharacter === true,
			promptText: "And make sure they have a name",
			invalidText: "You don't have a name for your main character. We can make one up for you.",
		});
		items.push({
			key: TopicChecklist.CHARACTER_QTY,
			title: "Characters",
			isDefined: this.topicAnalysis?.numberOfCharacters !== undefined,
			isValid:
				this.topicAnalysis?.numberOfCharacters !== undefined &&
				this.topicAnalysis.numberOfCharacters >= 1 &&
				this.topicAnalysis.numberOfCharacters <= 3,
			promptText: "Name more characters, or add manually later",
			invalidText: "You maybe have too many or too few characters. 1-3 is the optimal number.",
		});
		items.push({
			key: TopicChecklist.PLOT,
			title: "Plot",
			isDefined: this.topicAnalysis?.presenceOfPlot !== undefined,
			isValid: this.topicAnalysis?.presenceOfPlot === true,
			promptText: 'Mention a plot - "James wants to be a pilot", "Anna loves horses"',
			invalidText: "You don't have the start of a plot, but don't worry we can take care of this if you like.",
		});
		items.push({
			key: TopicChecklist.SIMPLE,
			title: "Content safe",
			isDefined: this.topicAnalysis?.copyright !== undefined,
			isValid: this.topicAnalysis?.copyright === false,
			promptText: "Avoid topics that could be blocked by content filters, such as copyrighted characters",
			invalidText: "Its likely this topic could run into a copyright or content filter issue.",
		});
		return items;
	}
	hasPassedTopicAnalysis(): boolean {
		return (
			this.topicAnalysis !== undefined &&
			this.topicAnalysis.simple &&
			this.topicAnalysis.mainCharacter &&
			this.topicAnalysis.namedCharacter &&
			this.topicAnalysis.numberOfCharacters >= 1 &&
			this.topicAnalysis.numberOfCharacters <= 3 &&
			this.topicAnalysis.presenceOfPlot &&
			!this.topicAnalysis.copyright
		);
	}
	setToAndFrom(to: string, from: string) {
		this.to = to;
		this.from = from;
	}
	getNextState(): number | undefined {
		for (const [key, value] of Object.entries(StoryStatus)) {
			if (typeof value === "number" && value > this.status) {
				return value as number;
			}
		}
		return undefined;
	}
	protected setStoryState(status: StoryStatus): void {
		if (
			status <= StoryStatus.VISUAL_DESCRIPTIONS_COVER ||
			status <= StoryStatus.VISUAL_DESCRIPTIONS_PAGES ||
			status <= StoryStatus.IMAGES_GENERATED ||
			status <= StoryStatus.COVER_IMAGES_GENERATED ||
			status <= StoryStatus.IMAGES_ACCEPTED ||
			status <= StoryStatus.IMAGES_ENLARGED
		) {
			//Do nothing
		}
		if (
			status <= StoryStatus.STORY_SET ||
			status <= StoryStatus.STORY_WRITTEN ||
			status <= StoryStatus.IMAGE_PROMPT_SET_CHOSEN ||
			status <= StoryStatus.CHARACTERS_APPEARANCE_SET
		) {
			if (this._frontCover !== undefined) this.markFieldDirty("_frontCover");
			if (this._backCover !== undefined) this.markFieldDirty("_backCover");
			if (this._storyImagePages !== undefined) this.markFieldDirty("_storyImagePages");
			this._frontCover = undefined;
			this._backCover = undefined;
			this._storyImagePages = undefined;
		}
		if (status <= StoryStatus.DEDICATION_ADDED) {
			if (this._storyPages !== undefined) this.markFieldDirty("_storyPages");
			this._storyPages = undefined;
		}
		if (status <= StoryStatus.CHARACTERS_SET) {
			if (this._to !== undefined) this.markFieldDirty("_to");
			if (this._from !== undefined) this.markFieldDirty("_from");
			if (this._dedication !== undefined) this.markFieldDirty("_dedication");
			this._to = undefined;
			this._from = undefined;
			this._dedication = undefined;
		}
		if (status <= StoryStatus.TITLE_OPTIONS_SET) {
			if (this._characters !== undefined) this.markFieldDirty("_characters");
			if (this._charactersToExclude !== undefined) this.markFieldDirty("_charactersToExclude");
			this._characters = undefined;
			this._charactersToExclude = undefined;
		}
		if (status <= StoryStatus.TOPIC_SET) {
			if (this._titleOptions !== undefined) this.markFieldDirty("_titleOptions");
			if (this._selectedTitle !== undefined) this.markFieldDirty("_selectedTitle");
			this._titleOptions = undefined;
			this._selectedTitle = undefined;
		}
		if (status <= StoryStatus.NOT_STARTED) {
			if (this._topicAnalysis !== undefined) this.markFieldDirty("_topicAnalysis");
			if (this._topic !== undefined) this.markFieldDirty("_topic");
			if (this._promptSetKey !== undefined) this.markFieldDirty("_promptSetKey");
			this._promptSetKey = undefined;
			this._topicAnalysis = undefined;
			this._topic = undefined;
		}
		if (status !== this.status) this.status = status;
	}
	getPageCount(mode: "total" | "content" | "text" | "images" = "images") {
		if (!this.storyPages) return 0;
		switch (mode) {
			case "total":
				return this.storyPages.length * 2 + 4;
			case "content":
				return this.storyPages.length * 2 + 2;
			case "images":
			case "text":
			default:
				return this.storyPages.length;
		}
	}
	isPageReady(pageNumber: "front" | "back" | "dedication" | "end" | number, excludeImage: boolean): boolean {
		const imageReady = !!this.getImageForPage(pageNumber);
		switch (pageNumber) {
			case "front":
				return (imageReady || excludeImage) && !!this.title && !!this.from;
			case "back":
				return imageReady || excludeImage;
			case "dedication":
				return !!this.dedication;
			case "end":
				return true;
			default:
				return (
					(imageReady || excludeImage) &&
					!!this.storyPages &&
					this.storyPages.length >= pageNumber &&
					!!this.storyPages[pageNumber - 1] &&
					!!this.storyPages[pageNumber - 1].textOptions
				);
		}
	}
	getTextPageCount(mode: "total" | "content") {
		let count = 0;
		switch (mode) {
			case "total":
				count += this.isPageReady("dedication", false) ? 1 : 0;
				count += 1; //End page is always ready
			case "content":
			default:
				const indices = Array.from({ length: this.getPageCount("text") }, (v, i) => i + 1);
				count += indices.filter((val) => this.isPageReady(val, true)).length;
		}
		return count;
	}
	getImagePageCount(mode: "total" | "content") {
		let count = 0;
		switch (mode) {
			case "total":
				count += this.isPageReady("front", false) ? 1 : 0;
				count += this.isPageReady("back", false) ? 1 : 0;
			case "content":
			default:
				const indices = Array.from({ length: this.getPageCount("images") }, (v, i) => i + 1);
				count += indices.filter((val) => this.isPageReady(val, false)).length;
		}
		return count;
	}
	getImagePagesYetToBeGenerated(mode: "total" | "content"): ("front" | "back" | number)[] {
		const pages: ("front" | "back" | number)[] = [];
		switch (mode) {
			case "total":
				if (!this.isPageReady("front", false)) pages.push("front");
				if (!this.isPageReady("back", false)) pages.push("back");
			case "content":
			default:
				const indices = Array.from({ length: this.getPageCount("images") }, (v, i) => i + 1);
				indices.map((val) => {
					if (!this.isPageReady(val, false)) pages.push(val);
				});
		}
		return pages;
	}
	getNextImagePageNumber(startingPageNumber: PageNumberType) {
		const startingArrayIndex = pageNumberArray.indexOf(startingPageNumber);
		if (startingArrayIndex === -1) return undefined;
		for (let x = startingArrayIndex + 1; x < pageNumberArray.length; x++) {
			if (this.getImageForPage(pageNumberArray[x])) return pageNumberArray[x];
		}
		return undefined;
	}
	isComplete(mode: "total" | "content", excludeImage: boolean = false) {
		let complete = true;
		switch (mode) {
			case "total":
				complete = this.isPageReady("front", excludeImage);
				complete = complete && this.isPageReady("back", excludeImage);
				complete = complete && this.isPageReady("dedication", excludeImage);
			case "content":
			default:
				const indices = Array.from({ length: this.getPageCount("images") }, (v, i) => i + 1);
				return complete && indices.filter((val) => this.isPageReady(val, excludeImage)).length === indices.length;
		}
	}
}
